// src/components/common/Sidebar.jsx - Redesigned with better UX
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItemIcon, ListItemText,
  Collapse, Divider, Typography, Box, ListItemButton,
  useTheme, Chip, alpha, Badge
} from '@mui/material';
import {
  Home as HomeIcon,
  ExpandLess, ExpandMore,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  Factory as FactoryIcon,
  Article as ArticleIcon,
  Lightbulb as InnovationIcon,
  Store as MarketplaceIcon,
  People as PeopleIcon,
  AccountCircle as ProfileIcon,
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  Assessment as AnalyticsIcon,
  Description as ReportIcon,
  ViewList as AllIndustriesIcon,
  Message as MessageIcon,
  Link as ConnectionIcon,
  School as ExpertIcon,
  Assignment as ApplicationsIcon
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import { getUnreadCount } from '../../services/message';

// Import sidebar logo
import logoDark from '../../assets/logo-white.png';
import logoLight from '../../assets/logo-black.png';

const drawerWidth = 280;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(180deg, ${theme.palette.grey[900]} 0%, ${alpha(theme.palette.grey[900], 0.98)} 100%)`
      : `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
    backdropFilter: 'blur(20px)',
    borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: theme.palette.mode === 'dark'
      ? `4px 0 24px ${alpha('#000', 0.3)}`
      : `4px 0 24px ${alpha(theme.palette.primary.main, 0.08)}`,
    height: '100vh',
    position: 'fixed',
    zIndex: theme.zIndex.drawer,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: alpha(theme.palette.primary.main, 0.3),
      borderRadius: 4,
      '&:hover': {
        background: alpha(theme.palette.primary.main, 0.5),
      }
    }
  },
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2.5, 2.5),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: theme.palette.primary.contrastText,
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at top right, ${alpha('#fff', 0.15)}, transparent 60%)`,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200%',
    height: '200%',
    background: `radial-gradient(circle, ${alpha('#00E5FF', 0.1)}, transparent 70%)`,
    pointerEvents: 'none',
  }
}));

const Logo = styled('img')({
  height: 40,
  width: 'auto',
  maxWidth: '100%',
  marginBottom: 8,
  position: 'relative',
  zIndex: 1,
  objectFit: 'contain',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  }
});

const UserSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2.5, 2.5),
  margin: theme.spacing(2, 1.5),
  borderRadius: theme.spacing(1.5),
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.05)})`
    : `linear-gradient(135deg, ${alpha('#fff', 0.9)}, ${alpha(theme.palette.primary.main, 0.03)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
    borderColor: alpha(theme.palette.primary.main, 0.25),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: `${theme.spacing(1.5)} ${theme.spacing(1.5)} 0 0`,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2, 2.5, 1, 2.5),
  fontSize: '0.75rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: theme.spacing(2.5),
    width: '40px',
    height: '2px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
    borderRadius: '2px',
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(1.5),
  margin: theme.spacing(0.5, 1.5),
  padding: theme.spacing(1.25, 1.5),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '3px',
    height: '100%',
    background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.08),
    transform: 'translateX(6px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    '&::before': {
      opacity: 0.5,
    },
    '& .MuiListItemIcon-root': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  },
  '&.active': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
    fontWeight: 600,
    '&::before': {
      opacity: 1,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
      transform: 'scale(1.1)',
      filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.4)})`,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 700,
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    }
  }
}));

const CategoryButton = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(1.5),
  margin: theme.spacing(0.5, 1.5),
  padding: theme.spacing(1.5, 1.5),
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  fontWeight: 700,
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at top left, ${alpha(theme.palette.primary.main, 0.15)}, transparent 70%)`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.secondary.main, 0.12)})`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
    '&::before': {
      opacity: 1,
    }
  }
}));

const Sidebar = ({ open, onClose, isAuthenticated, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);
  const [openCategories, setOpenCategories] = useState({
    industries: true,
  });

  const isDarkMode = theme.palette.mode === 'dark';
  const sidebarLogo = isDarkMode ? logoDark : logoLight;
  const isAdmin = user && (user.userType === 'admin' || user.role === 'admin');

  // Load unread message count
  useEffect(() => {
    const loadUnreadCount = async () => {
      if (isAuthenticated && !isAdmin) {
        try {
          const count = await getUnreadCount();
          setUnreadCount(count);
        } catch (error) {
          console.error('Error loading unread count:', error);
        }
      }
    };

    loadUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isAdmin]);

  if (!isAuthenticated) {
    return null;
  }

  const handleCategoryClick = (category) => {
    setOpenCategories({
      ...openCategories,
      [category]: !openCategories[category]
    });
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const mainNavItems = [
    { label: 'Home', icon: <HomeIcon />, path: '/home' },
    { label: 'All Industries', icon: <AllIndustriesIcon />, path: '/industries/all' },
    { label: 'Marketplace', icon: <MarketplaceIcon />, path: '/marketplace' },
    { label: 'Members', icon: <PeopleIcon />, path: '/members' },
    { label: 'Experts', icon: <ExpertIcon />, path: '/experts' },
    { label: 'Articles', icon: <ArticleIcon />, path: '/articles' },
  ];

  const userNavItems = [
    { label: 'My Profile', icon: <ProfileIcon />, path: '/profile' },
    { label: 'My Connections', icon: <ConnectionIcon />, path: '/connections' },
    {
      label: 'Messages',
      icon: <MessageIcon />,
      path: '/messages',
      badge: unreadCount > 0 ? unreadCount : null
    },
    { label: 'My Applications', icon: <ApplicationsIcon />, path: '/my-applications' },
  ];

  const analyticsNavItems = [
    { label: 'Analytics', icon: <AnalyticsIcon />, path: '/industries/analytics' },
    { label: 'Reports', icon: <ReportIcon />, path: '/industries/report' },
  ];

  const industryCategories = [
    {
      title: 'Service & Infrastructure',
      items: [
        { name: 'ICT', id: 'ict' },
        { name: 'Finance', id: 'finance' },
        { name: 'Healthcare', id: 'healthcare' },
        { name: 'Tourism', id: 'tourism' },
      ]
    },
    {
      title: 'Mature Industries',
      items: [
        { name: 'Petroleum', id: 'petroleum' },
        { name: 'Petrochemicals', id: 'petrochemicals' },
        { name: 'Mining', id: 'mining' },
        { name: 'Food & Beverages', id: 'food-beverages' },
      ]
    },
    {
      title: 'Localization',
      items: [
        { name: 'Pharmaceutical', id: 'pharmaceutical' },
        { name: 'Chemicals', id: 'chemicals' },
        { name: 'Automotive', id: 'automotive' },
        { name: 'Medical Devices', id: 'medical-devices' },
      ]
    }
  ];

  return (
    <StyledDrawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {/* Header */}
      <SidebarHeader>
        <Logo src={sidebarLogo} alt="Value Chain Global" />
        <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 600, position: 'relative', zIndex: 1 }}>
          Explore Smart Economy
        </Typography>
      </SidebarHeader>

      {/* User Info */}
      {user && (
        <UserSection>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              mb: 0.5,
              background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {`${user.firstName} ${user.lastName}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {user.title || 'Member'}
            </Typography>
            {isAdmin && (
              <Chip
                label="Admin"
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  color: '#fff',
                  fontWeight: 700,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`,
                }}
              />
            )}
          </Box>
        </UserSection>
      )}

      {/* Main Navigation */}
      <Box sx={{ py: 1 }}>
        <SectionTitle>Main Menu</SectionTitle>
        <List component="nav" sx={{ px: 0 }}>
          {mainNavItems.map((item) => (
            <StyledListItemButton
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={isActivePath(item.path) ? 'active' : ''}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.95rem' }}
              />
            </StyledListItemButton>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Industries */}
      <Box sx={{ py: 1 }}>
        <CategoryButton onClick={() => handleCategoryClick('industries')}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <BusinessIcon color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Industries by Category"
            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
          />
          {openCategories.industries ? <ExpandLess /> : <ExpandMore />}
        </CategoryButton>

        <Collapse in={openCategories.industries} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 2 }}>
            {industryCategories.map((category) => (
              <Box key={category.title} sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    pl: 2.5,
                    fontWeight: 600,
                    color: 'text.secondary',
                    display: 'block',
                    mb: 0.5
                  }}
                >
                  {category.title}
                </Typography>
                <List component="div" disablePadding>
                  {category.items.map((industry) => (
                    <StyledListItemButton
                      key={industry.id}
                      sx={{ pl: 4, py: 0.75 }}
                      onClick={() => handleNavigation(`/industry/${industry.id}`)}
                      className={location.pathname.includes(industry.id) ? 'active' : ''}
                    >
                      <ListItemText
                        primary={industry.name}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                      />
                    </StyledListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* User Section */}
      <Box sx={{ py: 1 }}>
        <SectionTitle>My Account</SectionTitle>
        <List component="nav" sx={{ px: 0 }}>
          {userNavItems.map((item) => (
            <StyledListItemButton
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={isActivePath(item.path) ? 'active' : ''}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.95rem' }}
              />
            </StyledListItemButton>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Analytics & Reports */}
      <Box sx={{ py: 1 }}>
        <SectionTitle>Insights</SectionTitle>
        <List component="nav" sx={{ px: 0 }}>
          {analyticsNavItems.map((item) => (
            <StyledListItemButton
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={isActivePath(item.path) ? 'active' : ''}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.95rem' }}
              />
            </StyledListItemButton>
          ))}
        </List>
      </Box>

      {/* Admin Section */}
      {isAdmin && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ py: 1 }}>
            <SectionTitle sx={{ color: 'error.main' }}>Administration</SectionTitle>
            <StyledListItemButton
              onClick={() => handleNavigation('/admin/dashboard')}
              sx={{
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.15),
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AdminIcon sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Admin Panel"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: 'error.main'
                }}
              />
            </StyledListItemButton>
          </Box>
        </>
      )}

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          mt: 'auto',
          textAlign: 'center',
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(180deg, transparent, ${alpha(theme.palette.primary.main, 0.03)})`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            opacity: 0.8,
          }}
        >
          © 2025 Value Chain Global
        </Typography>
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;
